import { formatDate } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { Alert } from "@models/alerts/alert";
import { AlertStatus } from "@models/alerts/alerts-enums";
import { EntityType } from "@models/common/entity-type.enum";
import { Company } from "@models/companies/company";
import { CompanyRecord } from '@models/companies/company-record';
import { Entity } from "@models/companies/entity";
import { IndividualDto } from '@models/individuals/individual-dto';
import { IndividualRecord } from '@models/individuals/Individual-record';
import { Trust } from "@models/trusts/trust";
import {
  AlertDetailsContentComponent
} from "@pages/alerts/components/alert-details-content/alert-details-content.component";
import { ITagsEntity } from "@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.constant";
import { CommonOffcanvasComponent } from "@shared/components/commons/common-offcanvas/common-offcanvas.component";
import {
  OffcanvasPaginationTitleComponent
} from "@shared/components/commons/common-offcanvas/components/offcanvas-pagination-title/offcanvas-pagination-title.component";
import {
  AgAlertsCategoryComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-category/ag-alerts-category.component";
import {
  AgAlertsStatusComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-status/ag-alerts-status.component";
import {
  AgAlertsTitleComponent
} from "@shared/components/grid/ag-cells/alerts/ag-alerts-title/ag-alerts-title.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import {
  CreateEditNoteControlComponent
} from "@shared/components/notes/components/create-edit-note-control/create-edit-note-control.component";
import { AlertsTypePipe } from "@shared/pipes/alertsTypePipe";
import { PageSizeService } from "@shared/services/page-size.service";
import { Guid } from "@shared/utils/guid.helper";
import { ColDef, GridApi, IRowNode, ValueFormatterParams } from "ag-grid-community";

@Component({
  selector: 'app-alerts-list',
  standalone: true,
  imports: [
    CommonOffcanvasComponent,
    AlertsTypePipe,
    ListGridClientSideComponent,
    OffcanvasPaginationTitleComponent,
    AlertDetailsContentComponent,
  ],
  providers: [PageSizeService],
  templateUrl: 'alerts-list.component.html',
  styleUrl: 'alerts-list.component.scss'
})
export class AlertsListComponent extends CommonOffcanvasComponent implements OnInit {
  pageSizeService = inject(PageSizeService);
  destroyRef = inject(DestroyRef);

  @ViewChild(CreateEditNoteControlComponent) createEditNoteControl!: CreateEditNoteControlComponent;

  @Input({ required: true, alias: 'alerts' }) set alertsSetter(value: Alert[]) {
    if (value?.length) {
      this.alertsList = value.filter((alert) => alert.status != AlertStatus.Archived);
      this.totalRows = this.alertsList.length;

      setTimeout(() => this.onSortChanged());
    }
  }

  @Input() set entity(value: Entity | Company | CompanyRecord | Trust | undefined | null) {
    if (value && value.entityId !== Guid.EmptyGuid) {
      this._entityOrIndividual = value;
    }
  }

  get entity(): ITagsEntity | null {
    if (this.entityType !== null) {
      return this._entityOrIndividual;
    }

    return null;
  }

  @Input() set individual(value: IndividualDto | IndividualRecord | null | undefined) {
    if (value && value.individualId !== Guid.EmptyGuid) {
      this._entityOrIndividual = value;
    }
  }

  @Input() entityType: EntityType | null = null;

  @Output() alertReaded = new EventEmitter<string>();


  $selectedAlert = signal<Alert | null>(null);

  private _entityOrIndividual: Entity | Company | CompanyRecord | Trust | IndividualDto | IndividualRecord | null = null;

  gridApi!: GridApi;
  alertsList: Alert[] = [];
  totalRows = 0;
  currentAlertIndex = 0;
  readonly alertContainerWidth = '856px';
  readonly defaultPageSize = PageSizeService.defaultPageSize;
  colDefs: ColDef[] = [];
  alertsListRowSelection = BaseListGridComponent.noCheckboxesRowSelection;

  ngOnInit(): void {
    this.setGridConfig();
  }

  setGridConfig(): void {
    const colDefs: ColDef[] = [
      {
        headerName: 'Date Created',
        sort: 'desc',
        field: 'createdOn',
        width: 136,
        maxWidth: 136,
        valueFormatter: (params: ValueFormatterParams<Alert, Date>) => {
          return formatDate(params.value ?? '', 'dd MMM yyyy', 'en-US');
        },
        menuTabs: ['filterMenuTab'],
      },
      {
        headerName: 'Description',
        field: 'alertDetail.title',
        flex: 1,
        width: 280,
        autoHeight: true,
        cellClass: 'cursor-pointer',
        cellRenderer: AgAlertsTitleComponent,
        cellRendererParams: {
          openAlert: this.openAlert.bind(this),
        },
        menuTabs: ['filterMenuTab']
      },
      {
        headerName: 'Type',
        field: 'type',
        cellRenderer: AgAlertsCategoryComponent,
        width: 215,
        maxWidth: 215,
        menuTabs: ['filterMenuTab']
      },
      {
        headerName: 'State',
        field: 'status',
        width: 125,
        maxWidth: 125,
        cellRenderer: AgAlertsStatusComponent,
        menuTabs: ['filterMenuTab']
      },
    ];

    this.colDefs = colDefs.map((colDef) => {
      colDef.suppressHeaderMenuButton = true;
      return colDef;
    });
  }

  onGridReady(gridApi: GridApi): void {
    this.gridApi = gridApi;
  }

  onSortChanged(): void {
    // rebuilds data source according to current sorted view
    const sortedData: Alert[] = [];
    const rowCount = this.alertsList.length;

    for (let i = 0; i < rowCount; i++) {
      const rowNode: IRowNode<Alert> | undefined = this.gridApi.getDisplayedRowAtIndex(i);

      if (rowNode && rowNode.data) {
        sortedData.push(rowNode.data);
      }
    }

    this.alertsList = sortedData;
  }

  openAlert(data: { alertRecord: Alert, index: number }): void {
    this.currentAlertIndex = data.index;
    this.$selectedAlert.set(data.alertRecord);
  }

  backToAlerts(): void {
    this.$selectedAlert.set(null);
  }

  closeAlerts(): void {
    this.closeOffcanvas();
    this.$selectedAlert.set(null);
  }

  previousAlert(): void {
    if (this.currentAlertIndex > 0) {
      this.currentAlertIndex = this.currentAlertIndex - 1;
      this.updateSelectedAlert();
    }
  }

  nextAlert(): void {
    if (this.currentAlertIndex < this.totalRows) {
      this.currentAlertIndex = this.currentAlertIndex + 1;
      this.updateSelectedAlert();
    }
  }

  onAlertReaded(alertId: string): void {
    this.alertReaded.emit(alertId);
  }

  private updateSelectedAlert(): void {
    this.$selectedAlert.set(this.alertsList[this.currentAlertIndex]);
  }

  updateAlert(alert: Alert): void {
    this.alertsList[this.currentAlertIndex] = alert;
    this.gridApi.applyTransactionAsync({ update: [alert] });
    this.$selectedAlert.set(alert);
  }
}
